import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { MainLayout } from '../components/shared/MainLayout';
import {
  ArrowUpRight, ArrowDownRight, CaretRight, Users, Sparkle,
  CurrencyDollar, ChartLineUp, Warning, Gauge, Receipt, ChatCircle, FilePdf, SealCheck,
  TrendDown, DownloadSimple, Lightning, Timer, Path,
} from '@phosphor-icons/react';

/**
 * Stage 3 — Growth Operating System
 * Premium founder mission-control dashboard.
 *
 * Route: /founder/mission-control
 * Data:  GET /api/founder/stage3-dashboard  (admin/founder role)
 */

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  if (typeof n === 'number' && Number.isFinite(n)) {
    if (Math.abs(n) >= 1000) return n.toLocaleString('en-AU', { maximumFractionDigits: 0 });
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(2);
  }
  return String(n);
};

const fmtCurrency = (n, currency = 'AUD') => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency, maximumFractionDigits: 0,
  }).format(n);
};

// ─────────────────────────────────────────────────────────────────────────────
// Single-metric tile
// ─────────────────────────────────────────────────────────────────────────────
const MetricTile = ({ label, value, hint, icon: Icon, tone = 'neutral', testid }) => {
  const tones = {
    neutral: 'bg-white border-[#E5DFD0]',
    positive: 'bg-emerald-50/70 border-emerald-100',
    watchful: 'bg-amber-50/70 border-amber-100',
    critical: 'bg-rose-50/70 border-rose-100',
    hero: 'bg-[#0E1C2E] text-white border-[#0E1C2E]',
  };
  const isHero = tone === 'hero';
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border ${tones[tone]} p-5 flex flex-col gap-2 min-h-[112px]`}
    >
      <div className={`flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] ${isHero ? 'text-white/60' : 'text-slate-400'}`}>
        {Icon && <Icon size={12} weight="duotone" />}
        <span>{label}</span>
      </div>
      <div className={`font-serif text-[28px] sm:text-[32px] leading-none tracking-[-0.012em] tabular-nums ${isHero ? 'text-white' : 'text-[#0E1C2E]'}`}>
        {value}
      </div>
      {hint && (
        <p className={`text-[12px] leading-relaxed ${isHero ? 'text-white/60' : 'text-slate-500'}`}>
          {hint}
        </p>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Funnel block
// ─────────────────────────────────────────────────────────────────────────────
const FunnelChart = ({ steps, label }) => {
  if (!steps?.length) return null;
  const max = Math.max(...steps.map(s => s.count || 0), 1);
  return (
    <div data-testid="stage3-funnel" className="bg-white border border-[#E5DFD0] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 mb-1">Funnel</p>
          <p className="font-serif text-[20px] leading-tight text-[#0E1C2E]">Landing to paid · <span className="text-slate-400">{label}</span></p>
        </div>
      </div>
      <div className="space-y-3">
        {steps.map((s, i) => {
          const w = max > 0 ? Math.max(4, Math.round((s.count / max) * 100)) : 4;
          return (
            <div key={s.step} className="group" data-testid={`funnel-step-${i}`}>
              <div className="flex items-baseline justify-between text-[12px] mb-1">
                <span className="text-[#0E1C2E] font-medium">{s.step}</span>
                <span className="text-slate-400 tabular-nums">
                  {fmt(s.count)}
                  {i > 0 && s.pct_of_prev != null && (
                    <span className="ml-2 text-slate-300">· {s.pct_of_prev}%</span>
                  )}
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#235347]/80 to-[#2FB3A0]/70 rounded-full transition-all"
                  style={{ width: `${w}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stage 5 — Conversion tiles
// ─────────────────────────────────────────────────────────────────────────────
const CONVERSION_LABELS = [
  { key: 'signup_conversion_pct',     label: 'Signup conversion',  hint: 'Visitors → signed up' },
  { key: 'onboarding_completion_pct', label: 'Onboarding done',    hint: 'Signups → finished onboarding' },
  { key: 'first_chat_pct',            label: 'First chat',         hint: 'Signups → sent first message' },
  { key: 'first_upload_pct',          label: 'First upload',       hint: 'Signups → added first report' },
  { key: 'd1_retention_pct',          label: 'Day 1 retention',    hint: 'Returned within 24h' },
  { key: 'd7_retention_pct',          label: 'Day 7 retention',    hint: 'Returned within a week' },
  { key: 'trial_to_paid_pct',         label: 'Trial → paid',       hint: 'Signups → active subscription' },
];

const ConversionTile = ({ label, hint, value, testid }) => {
  // Cap obvious display oddities (>100%) so the tile doesn't go off-grid in
  // edge cases where landing event capture lags signup creation (e.g. demo
  // accounts, admin-seeded users). The raw number stays in the CSV export.
  const display = (typeof value === 'number')
    ? `${Math.min(999.9, value).toFixed(1)}%`
    : '—';
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#E5DFD0] bg-white p-5 flex flex-col gap-1 min-h-[112px]"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <div className="font-serif text-[28px] sm:text-[32px] leading-none tracking-[-0.012em] tabular-nums text-[#0E1C2E] mt-1">
        {display}
      </div>
      <p className="text-[12px] text-slate-500 leading-relaxed mt-1">{hint}</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stage 5 — Biggest drop-off banner
// ─────────────────────────────────────────────────────────────────────────────
const BiggestDropoffBanner = ({ dropoff }) => {
  if (!dropoff || (dropoff.lost || 0) === 0) return null;
  return (
    <div
      data-testid="stage5-biggest-dropoff"
      className="rounded-2xl border border-amber-200/80 bg-amber-50/70 p-5 flex items-start gap-3"
    >
      <TrendDown size={22} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">
          Biggest drop-off · last 30 days
        </p>
        <p className="font-serif text-[20px] sm:text-[22px] leading-tight text-[#0E1C2E] mt-1">
          <em className="italic">{dropoff.from}</em> → {dropoff.to}
        </p>
        <p className="text-[12.5px] text-slate-600 mt-1.5 leading-relaxed">
          You lost <span className="font-semibold tabular-nums text-[#0E1C2E]">{dropoff.lost}</span>
          {' '}users here ({dropoff.drop_pct}%). This is where focused product effort moves the needle next.
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stage 5 — Sparkline (inline SVG, no chart library)
// ─────────────────────────────────────────────────────────────────────────────
const Sparkline = ({ points, color = '#2FB3A0', height = 36, label, total, testid }) => {
  const data = (points || []).map((p) => p.count || 0);
  const max = Math.max(...data, 1);
  const w = 100;
  const h = height;
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const path = data
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(2)} ${(h - (v / max) * (h - 4) - 2).toFixed(2)}`)
    .join(' ');
  // Build area (stroke + filled gradient under)
  const area = data.length
    ? `${path} L ${(w).toFixed(2)} ${h} L 0 ${h} Z`
    : '';
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#E5DFD0] bg-white p-5 flex flex-col gap-2 min-h-[112px]"
    >
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
        <p className="font-serif text-[22px] tabular-nums text-[#0E1C2E] leading-none">
          {total ?? data.reduce((a, b) => a + b, 0)}
        </p>
      </div>
      <p className="text-[11px] text-slate-500 mt-0.5">last 14 days</p>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full mt-1" preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id={`sparkfill-${label}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {area && <path d={area} fill={`url(#sparkfill-${label})`} stroke="none" />}
        {path && <path d={path} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />}
      </svg>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stage 8 — Experimentation engine section
// ─────────────────────────────────────────────────────────────────────────────
const CONFIDENCE_COLOR = {
  '99% confident': '#1f7d4d',
  '95% confident': '#1f7d4d',
  '90% confident': '#b25d04',
  '80% confident': '#b25d04',
  'Inconclusive':  '#64748b',
  'Need more data': '#94a3b8',
  'Control': '#64748b',
};

const ConfidenceBadge = ({ label }) => {
  const color = CONFIDENCE_COLOR[label] || '#64748b';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums"
      style={{
        color,
        backgroundColor: `${color}1a`,
      }}
      data-testid="experiment-confidence-badge"
    >
      {label}
    </span>
  );
};

const ExperimentCard = ({ exp }) => {
  const totalExposures = exp.variants.reduce((acc, r) => acc + (r.exposures || 0), 0);
  const recommendationColor = exp.winner
    ? '#1f7d4d'
    : (exp.recommendation || '').startsWith('Promising')
      ? '#b25d04'
      : (exp.recommendation || '').startsWith('Control still')
        ? '#0E1C2E'
        : '#64748b';

  return (
    <div
      className="rounded-2xl border border-[#E5DFD0] bg-white p-5"
      data-testid={`experiment-${exp.key}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            {exp.key}
          </p>
          <p className="text-[13px] text-[#0E1C2E] leading-snug mt-1 max-w-md">
            {exp.description}
          </p>
        </div>
        <div className="text-right text-[11px] text-slate-500 tabular-nums shrink-0">
          {totalExposures} exposures
          <br />
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400">
            primary: {exp.primary_metric === 'completion_rate' ? 'completion' : 'pick rate'}
          </span>
        </div>
      </div>

      {/* Variant rows */}
      <div className="space-y-2 mb-4">
        {exp.variants.map((r) => {
          const isWinner = exp.winner && r.variant === exp.winner;
          return (
            <div
              key={r.variant}
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-xl border ${
                isWinner ? 'bg-emerald-50/60 border-emerald-200' : 'bg-[#FBFAF6] border-transparent'
              }`}
              data-testid={`experiment-${exp.key}-${r.variant}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[12.5px] font-medium ${r.is_control ? 'text-slate-500' : 'text-[#0E1C2E]'}`}>
                  {r.variant}{r.is_control && <span className="ml-1 text-[10px] uppercase tracking-[0.14em] text-slate-400">control</span>}
                </span>
                {isWinner && <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">WINNER</span>}
              </div>
              <div className="flex items-center gap-3 text-[11.5px] tabular-nums shrink-0">
                <span className="text-slate-500">{r.exposures}n</span>
                <span className="font-semibold text-[#0E1C2E]">{r.pick_rate_pct.toFixed(1)}%</span>
                {!r.is_control && r.lift_pct_points !== 0 && (
                  <span className={`text-[10.5px] ${r.lift_pct_points > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {r.lift_pct_points > 0 ? '+' : ''}{r.lift_pct_points}pp
                  </span>
                )}
                <ConfidenceBadge label={r.confidence_label} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommendation row */}
      <div className="flex items-start gap-2 text-[12.5px] leading-snug">
        <Lightning size={13} weight="duotone" className="flex-shrink-0 mt-[2px]" style={{ color: recommendationColor }} />
        <div>
          <span className="font-semibold" style={{ color: recommendationColor }}>{exp.recommendation}</span>
          <span className="text-slate-500"> — {exp.recommendation_detail}</span>
        </div>
      </div>
    </div>
  );
};

const Stage8Section = ({ s }) => {
  if (!s || !Array.isArray(s.experiments) || s.experiments.length === 0) return null;
  const winnersCount = s.experiments.filter((e) => !!e.winner).length;
  return (
    <section className="mb-8" data-testid="stage8-experiments">
      <div className="flex items-baseline justify-between mb-3 px-0.5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Stage 8</p>
          <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight text-[#0E1C2E]">
            Experiment <em className="italic">engine.</em>
          </h2>
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {s.total_users_in_window || 0} users · {s.experiments.length} live tests
          {winnersCount > 0 && (
            <span className="ml-2 text-emerald-700 font-semibold">{winnersCount} winner{winnersCount === 1 ? '' : 's'}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {s.experiments.map((e) => (
          <ExperimentCard key={e.key} exp={e} />
        ))}
      </div>

      <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">
        Variants are deterministic per user (hash on user_id). Winners are only declared at ≥95% confidence
        and only after each variant clears its minimum sample size — protecting against tiny-N noise.
      </p>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stage 6+7 — Activation Engine analytics section
// ─────────────────────────────────────────────────────────────────────────────
const PATH_META = {
  upload:    { label: 'Upload',    accent: '#2FB3A0' },
  chat:      { label: 'Chat',      accent: '#0E1C2E' },
  condition: { label: 'Condition', accent: '#235347' },
  demo:      { label: 'Demo',      accent: '#7B61FF' },
};

function fmtSeconds(n) {
  if (n === null || n === undefined) return '—';
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

const Stage6Tile = ({ label, value, hint, testid, accent = '#0E1C2E' }) => (
  <div
    className="rounded-2xl border border-[#E5DFD0] bg-white p-5 flex flex-col gap-1 min-h-[112px]"
    data-testid={testid}
  >
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <div className="font-serif text-[28px] sm:text-[32px] leading-none tracking-[-0.012em] tabular-nums mt-1" style={{ color: accent }}>
      {value}
    </div>
    {hint && <p className="text-[12px] text-slate-500 leading-relaxed mt-1">{hint}</p>}
  </div>
);

const PathBar = ({ pathKey, picks, completionPct, totalPicks, testid }) => {
  const meta = PATH_META[pathKey] || { label: pathKey, accent: '#0E1C2E' };
  const share = totalPicks ? Math.round((picks / totalPicks) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="w-20 flex-shrink-0">
        <p className="text-[12.5px] font-semibold text-[#0E1C2E]">{meta.label}</p>
        <p className="text-[10.5px] text-slate-500 tabular-nums">{picks} pick{picks === 1 ? '' : 's'}</p>
      </div>
      <div className="flex-1 h-2 rounded-full bg-[#F4F1EB] overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.min(100, share)}%`, backgroundColor: meta.accent }}
        />
      </div>
      <div className="w-20 text-right flex-shrink-0">
        <p className="text-[13px] font-semibold tabular-nums text-[#0E1C2E]">{share}%</p>
        <p className="text-[10.5px] text-slate-500 tabular-nums">{completionPct}% done</p>
      </div>
    </div>
  );
};

const Stage6Section = ({ s }) => {
  const PATHS = ['upload', 'chat', 'condition', 'demo'];
  const totalPicks = PATHS.reduce((acc, p) => acc + (s.path_picks?.[p] || 0), 0);
  const ttfvOverall = s.time_to_first_value_seconds?.overall;
  const ttfvByPath = s.time_to_first_value_seconds?.by_path || {};

  return (
    <section className="mb-8" data-testid="stage6-activation">
      <div className="flex items-baseline justify-between mb-3 px-0.5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Stage 6+7</p>
          <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight text-[#0E1C2E]">
            Activation <em className="italic">engine.</em>
          </h2>
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {s.signups || 0} signups · {s.welcome_views || 0} saw welcome · {s.paused_users || 0} paused
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
        <Stage6Tile
          label="Welcome views"
          value={fmt(s.welcome_views)}
          hint="Reached the 4-card surface"
          testid="stage6-welcome-views"
        />
        <Stage6Tile
          label="Activation rate"
          value={`${(s.conversions?.signup_to_activation_pct ?? 0).toFixed(1)}%`}
          hint="Signups → picked a path"
          testid="stage6-activation-rate"
          accent="#2FB3A0"
        />
        <Stage6Tile
          label="First chat"
          value={`${(s.conversions?.signup_to_first_chat_pct ?? 0).toFixed(1)}%`}
          hint="Signups → sent first message"
          testid="stage6-first-chat"
        />
        <Stage6Tile
          label="First upload"
          value={`${(s.conversions?.signup_to_first_upload_pct ?? 0).toFixed(1)}%`}
          hint="Signups → added first report"
          testid="stage6-first-upload"
        />
        <Stage6Tile
          label="Time to value"
          value={fmtSeconds(ttfvOverall)}
          hint="Median across all paths"
          testid="stage6-ttfv"
          accent="#7B61FF"
        />
        <Stage6Tile
          label="Pick → value"
          value={`${(s.conversions?.activation_to_value_pct ?? 0).toFixed(1)}%`}
          hint="Picked path → saw something useful"
          testid="stage6-pick-to-value"
        />
      </div>

      {/* Per-path picks + completion bars */}
      <div className="rounded-2xl border border-[#E5DFD0] bg-white p-5 mb-5">
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Path performance · 30 days</p>
          <p className="text-[11px] text-slate-500 tabular-nums">{totalPicks} total picks</p>
        </div>
        <div className="space-y-3" data-testid="stage6-path-bars">
          {PATHS.map((p) => (
            <PathBar
              key={p}
              pathKey={p}
              picks={s.path_picks?.[p] || 0}
              completionPct={s.path_completion_rate?.[p] || 0}
              totalPicks={totalPicks}
              testid={`stage6-path-${p}`}
            />
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
          Bar = share of picks. Right-side `% done` = users who reached <em>first value</em> after picking that path.
        </p>
      </div>

      {/* Time-to-value per path + retention/conversion mini-grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2" data-testid="stage6-path-mini-grid">
        {/* TTFV per path */}
        <div className="rounded-2xl border border-[#E5DFD0] bg-white p-5">
          <div className="flex items-center gap-1.5 mb-3">
            <Timer size={11} weight="duotone" className="text-[#7B61FF]" />
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Time to value</p>
          </div>
          <div className="space-y-2">
            {PATHS.map((p) => {
              const meta = PATH_META[p];
              const v = ttfvByPath[p];
              return (
                <div key={p} className="flex items-center justify-between text-[13px]">
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.accent }} />
                    {meta.label}
                  </span>
                  <span className="font-semibold tabular-nums text-[#0E1C2E]">{fmtSeconds(v)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* D1 retention by path */}
        <div className="rounded-2xl border border-[#E5DFD0] bg-white p-5">
          <div className="flex items-center gap-1.5 mb-3">
            <Path size={11} weight="duotone" className="text-[#2FB3A0]" />
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Day 1 retention</p>
          </div>
          <div className="space-y-2">
            {PATHS.map((p) => {
              const meta = PATH_META[p];
              const r = s.d1_retention_by_path?.[p] || {};
              return (
                <div key={p} className="flex items-center justify-between text-[13px]">
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.accent }} />
                    {meta.label}
                  </span>
                  <span className="font-semibold tabular-nums text-[#0E1C2E]">
                    {(r.pct ?? 0).toFixed(0)}%
                    <span className="text-[10.5px] text-slate-400 ml-1 font-normal">({r.retained || 0}/{r.of || 0})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trial → paid by path */}
        <div className="rounded-2xl border border-[#E5DFD0] bg-white p-5">
          <div className="flex items-center gap-1.5 mb-3">
            <SealCheck size={11} weight="duotone" className="text-emerald-600" />
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Trial → paid</p>
          </div>
          <div className="space-y-2">
            {PATHS.map((p) => {
              const meta = PATH_META[p];
              const r = s.trial_conversion_by_path?.[p] || {};
              return (
                <div key={p} className="flex items-center justify-between text-[13px]">
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.accent }} />
                    {meta.label}
                  </span>
                  <span className="font-semibold tabular-nums text-[#0E1C2E]">
                    {(r.pct ?? 0).toFixed(0)}%
                    <span className="text-[10.5px] text-slate-400 ml-1 font-normal">({r.paid || 0}/{r.of || 0})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stripe events widget
// ─────────────────────────────────────────────────────────────────────────────
const StripeEventsWidget = ({ breakdown, total }) => {
  if (!breakdown) return null;
  return (
    <div data-testid="stripe-events-widget" className="bg-white border border-[#E5DFD0] rounded-2xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 mb-1">Stripe activity · last 24h</p>
          <p className="font-serif text-[20px] leading-tight text-[#0E1C2E]">
            {total === 0 ? 'Quiet' : `${total} event${total === 1 ? '' : 's'}`}
          </p>
        </div>
        <Receipt size={18} weight="duotone" className="text-slate-300" />
      </div>
      {breakdown.length === 0 ? (
        <p className="text-[13px] italic text-slate-400 leading-relaxed">
          No webhook traffic yet. Once customers subscribe, their events will appear here in near-real-time.
        </p>
      ) : (
        <ul className="space-y-2">
          {breakdown.map((e, i) => (
            <li key={i} className="flex items-center justify-between text-[13px]">
              <span className="text-[#0E1C2E] font-mono text-[12px]">{e.event_type}</span>
              <span className="tabular-nums text-slate-500">{e.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function FounderMissionControl() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stage6, setStage6] = useState(null);
  const [stage8, setStage8] = useState(null);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('30d'); // '30d' | '7d'

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/founder/stage3-dashboard`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.status === 403
          ? 'Founder access required to view this page.'
          : (e.response?.data?.detail || 'Failed to load dashboard.'));
      }
    })();
    // Stage 6+7 activation analytics — separate fetch so the page renders even
    // if this is slow (it does retention loops over user_ids).
    (async () => {
      try {
        const r = await axios.get(`${API}/founder/stage6-activation`);
        setStage6(r.data);
      } catch { /* dashboard still useful without it */ }
    })();
    // Stage 8 — experiments analytics. Independent fetch so the rest of the
    // dashboard renders even if the aggregator is slow.
    (async () => {
      try {
        const r = await axios.get(`${API}/founder/experiments`);
        setStage8(r.data);
      } catch { /* dashboard still useful without it */ }
    })();
  }, []);

  if (error) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto pt-16 text-center">
          <p className="font-serif italic text-[20px] text-[#0E1C2E] mb-2">{error}</p>
          <button
            onClick={() => navigate('/home')}
            className="text-[13px] text-[#235347] hover:underline"
          >
            Back to home →
          </button>
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-56 bg-[#ECE8E1] rounded" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[0,1,2,3,4,5,6,7].map(i => <div key={i} className="h-28 bg-[#ECE8E1]/60 rounded-2xl" />)}
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  const m = data.metrics;
  const hasUrgent = m.failed_payments > 0;

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto pb-10" data-testid="stage3-dashboard">

        {/* Header */}
        <header className="mb-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#235347] mb-2">Founder · Mission control</p>
          <h1 className="font-serif text-[32px] sm:text-[40px] lg:text-[44px] leading-[1.1] tracking-[-0.015em] text-[#0E1C2E]">
            Today in <em className="italic">Kindra.</em>
          </h1>
          <p className="text-[13px] text-slate-500 mt-2">
            Generated {new Date(data.generated_at).toLocaleString('en-AU', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
            <span className="mx-2 text-slate-300">·</span>
            <button
              onClick={() => window.location.reload()}
              className="text-[#235347] hover:underline"
              data-testid="stage3-refresh"
            >
              Refresh
            </button>
          </p>
        </header>

        {/* URGENT — only shows if failed payments exist */}
        {hasUrgent && (
          <div
            data-testid="stage3-urgent-banner"
            className="mb-6 bg-rose-50 border border-rose-200 rounded-2xl p-5 flex items-start gap-3"
          >
            <Warning size={22} weight="duotone" className="text-rose-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-[#0E1C2E] text-[14px]">
                {m.failed_payments} payment{m.failed_payments === 1 ? '' : 's'} need your attention
              </p>
              <p className="text-[12.5px] text-slate-600 mt-1">Customers in past_due state — card declined or needs update.</p>
            </div>
          </div>
        )}

        {/* ── Hero row: the numbers a founder glances at first ── */}
        <section data-testid="stage3-hero-metrics" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricTile label="MRR" value={fmtCurrency(m.mrr, 'AUD')} hint={`${fmt(m.paid_active)} paid customers · ARR ${fmtCurrency(m.arr, 'AUD')}`} icon={CurrencyDollar} tone="hero" testid="metric-mrr" />
          <MetricTile label="Paid active" value={fmt(m.paid_active)} hint="Stripe subscription active" icon={SealCheck} tone="neutral" testid="metric-paid-active" />
          <MetricTile label="Trial active" value={fmt(m.trial_active)} hint="Inside 30-day free window" icon={Users} tone="neutral" testid="metric-trial-active" />
          <MetricTile label="Founder members" value={fmt(m.founder_members)} hint="Lifetime access grants" icon={Sparkle} tone="neutral" testid="metric-founders" />
        </section>

        {/* ── Today row: cadence of product ── */}
        <section data-testid="stage3-today-metrics" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricTile label="Visitors today" value={fmt(m.visitors_today)} hint="Distinct landing views (UTC)" icon={ChartLineUp} tone={m.visitors_today > 0 ? 'positive' : 'neutral'} testid="metric-visitors" />
          <MetricTile label="Signups today" value={fmt(m.signups_today)} hint="New accounts created" icon={Users} tone={m.signups_today > 0 ? 'positive' : 'neutral'} testid="metric-signups" />
          <MetricTile label="Chats today" value={fmt(m.chats_today)} hint="User messages sent" icon={ChatCircle} tone="neutral" testid="metric-chats" />
          <MetricTile label="Reports today" value={fmt(m.reports_today)} hint="Uploaded documents" icon={FilePdf} tone="neutral" testid="metric-reports" />
        </section>

        {/* ── Stage 11: Live growth pulse (DAU · WAU · voice · trial→paid) ── */}
        <section data-testid="stage11-live-metrics" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricTile label="DAU" value={fmt(m.dau)} hint="Distinct active in last 24h" icon={Lightning} tone={m.dau > 0 ? 'positive' : 'neutral'} testid="metric-dau" />
          <MetricTile label="WAU" value={fmt(m.wau)} hint="Distinct active in last 7 days" icon={Users} tone={m.wau > 0 ? 'positive' : 'neutral'} testid="metric-wau" />
          <MetricTile label="Voice usage" value={`${m.voice_usage_pct ?? 0}%`} hint={`${fmt(m.voice_users_week || 0)} weekly users played a reply`} icon={Sparkle} tone={(m.voice_usage_pct || 0) > 0 ? 'positive' : 'neutral'} testid="metric-voice" />
          <MetricTile label="Trial → paid" value={`${m.trial_to_paid_pct ?? 0}%`} hint="30-day cohort conversion" icon={SealCheck} tone={(m.trial_to_paid_pct || 0) >= 15 ? 'positive' : 'neutral'} testid="metric-trial-to-paid" />
        </section>

        {/* ── Billing health row ── */}
        <section data-testid="stage3-billing-metrics" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <MetricTile
            label="Churn this month"
            value={`${fmt(m.churn_this_month)} · ${m.churn_pct}%`}
            hint={m.churn_this_month === 0 ? 'No one has canceled this month' : 'of month-start paid base'}
            icon={ArrowDownRight}
            tone={m.churn_this_month > 0 ? 'watchful' : 'positive'}
            testid="metric-churn"
          />
          <MetricTile
            label="Failed payments"
            value={fmt(m.failed_payments)}
            hint={m.failed_payments === 0 ? 'All subscriptions healthy' : 'Need customer action'}
            icon={Warning}
            tone={m.failed_payments > 0 ? 'critical' : 'positive'}
            testid="metric-failed-payments"
          />
          <MetricTile
            label="Stripe events · 24h"
            value={fmt(m.stripe_events_24h)}
            hint={m.stripe_events_24h === 0 ? 'Quiet webhook channel' : 'Billing lifecycle firing'}
            icon={Gauge}
            tone="neutral"
            testid="metric-stripe-events"
          />
        </section>

        {/* ── Funnel + Stripe breakdown side-by-side on desktop ── */}
        <section className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-6 mb-8">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Range</p>
              <div className="flex bg-slate-100 rounded-full p-0.5">
                {['7d', '30d'].map(r => (
                  <button
                    key={r}
                    onClick={() => setRange(r)}
                    data-testid={`range-${r}`}
                    className={`text-[11px] font-medium px-3 py-1 rounded-full transition-colors ${
                      range === r ? 'bg-white text-[#0E1C2E] shadow-sm' : 'text-slate-500 hover:text-[#0E1C2E]'
                    }`}
                  >
                    {r === '7d' ? 'Last 7 days' : 'Last 30 days'}
                  </button>
                ))}
              </div>
            </div>
            <FunnelChart steps={data.funnel[range]} label={range === '7d' ? '7 days' : '30 days'} />
          </div>
          <div className="lg:col-span-2">
            <StripeEventsWidget breakdown={data.stripe_events_breakdown} total={m.stripe_events_24h} />
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  STAGE 5 — Growth Intelligence Layer                            */}
        {/*  Conversions · Biggest drop-off · 14-day sparklines · CSV export */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {data.stage5 && (
          <section className="mb-8" data-testid="stage5-growth">
            <div className="flex items-baseline justify-between mb-3 px-0.5">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Stage 5</p>
                <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight text-[#0E1C2E]">
                  Growth <em className="italic">intelligence.</em>
                </h2>
              </div>
              <a
                href={`${API}/founder/stage5-export.csv`}
                download
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#0E1C2E] bg-white hover:bg-[#FBFAF6] border border-[#ECE8E1] hover:border-[#CFC8BB] rounded-full px-3.5 py-2 transition-colors"
                data-testid="stage5-export-csv"
              >
                <DownloadSimple size={13} weight="bold" />
                Export CSV
              </a>
            </div>

            {/* Biggest drop-off — surfaces the highest-leverage product fix */}
            {data.stage5.biggest_dropoff && (
              <div className="mb-5">
                <BiggestDropoffBanner dropoff={data.stage5.biggest_dropoff} />
              </div>
            )}

            {/* 14-day sparklines — visitors and signups side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <Sparkline
                points={data.stage5.visitors_14d}
                label="Visitors"
                color="#0E1C2E"
                testid="stage5-spark-visitors"
              />
              <Sparkline
                points={data.stage5.signups_14d}
                label="Signups"
                color="#2FB3A0"
                testid="stage5-spark-signups"
              />
            </div>

            {/* Conversions grid — 30-day window */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="stage5-conversions">
              {CONVERSION_LABELS.map((c) => (
                <ConversionTile
                  key={c.key}
                  label={c.label}
                  hint={c.hint}
                  value={data.stage5.conversions_30d?.[c.key]}
                  testid={`stage5-conv-${c.key}`}
                />
              ))}
            </div>
          </section>
        )}

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  STAGE 6+7 — Activation Engine analytics                         */}
        {/*  Welcome views · path picks · completion · TTFV · per-path D1/Trial */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {stage6 && <Stage6Section s={stage6} />}

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  STAGE 8 — Experimentation engine                                */}
        {/*  Per-experiment, per-variant rates + confidence + recommendation */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {stage8 && <Stage8Section s={stage8} />}

        {/* ── Quick links to existing founder tools ── */}
        <section className="flex flex-wrap gap-2" data-testid="stage3-quicklinks">
          {[
            { label: 'Growth dashboard', href: '/founder/growth' },
            { label: 'Intelligence logs', href: '/founder/intelligence' },
            { label: 'Founder home', href: '/founder' },
          ].map(x => (
            <button
              key={x.href}
              onClick={() => navigate(x.href)}
              className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-[#0E1C2E] bg-slate-50 hover:bg-slate-100 border border-slate-100 rounded-full px-3 py-1.5 transition-colors"
            >
              {x.label}
              <CaretRight size={11} />
            </button>
          ))}
        </section>
      </div>
    </MainLayout>
  );
}
